'use client';

/**
 * CHAT environment — Beast Mind chat layer, pixel-ported from the design
 * handoff (`Beast Mind.dc.html` chatLayer). Three columns:
 *   left  300px — PARALLEL SESSIONS rail (live /api/gw/sessions + pinned Beast/General)
 *   center      — session header (sigil · name · LIVE · MODEL · CTX) + the REAL
 *                 chat components hosted per the plan's fallback law (never a
 *                 rewrite of the streaming path): `Chat` for the pinned Beast
 *                 main chat, `SessionChat` for everything else. Scoped CSS
 *                 strips their classic chrome; transcript + input remain.
 *   right 380px — SESSION VITALS (live context/state/model per session)
 *
 * Voice console (BEAST MODE panel from the spec) ships separately — it hosts
 * the real BeastMode pipeline, visual states only.
 */
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { Chat } from '@/components/chat/Chat';
import { SessionChat } from '@/components/chat/SessionChat';
import { StatusBar } from '@/components/layout/StatusBar';
import { mono } from '@/future/primitives';

/* Beast Mode voice moved entirely to the orb (Boss 2026-07-11). ChatSurface no longer
   mounts its own recorder/panel — see the note in the right column. */

const GW = '/api/gw';
const BEAST = '__beast';
const GENERAL = '__general';

interface SessionInfo {
  slug: string;
  name: string;
  path: string;
  state: 'working' | 'idle' | 'hibernating';
  model?: { id: string } | null;
  contextUsage?: { tokens: number; contextWindow: number; percent: number } | null;
  lastError?: string;
}

/* spec palette — session sigils cycle through the handoff hues */
const HUES = [
  { hex: '#7de8ff', rgb: '125,232,255' },
  { hex: '#b49aff', rgb: '180,154,255' },
  { hex: '#ff9ad5', rgb: '255,154,213' },
  { hex: '#7db4ff', rgb: '125,180,255' },
  { hex: '#5df2b4', rgb: '93,242,180' },
  { hex: '#ffc36b', rgb: '255,195,107' },
];
const hueOf = (slug: string) => HUES[[...slug].reduce((a, c) => a + c.charCodeAt(0), 0) % HUES.length];
const iniOf = (name: string) => name.split(/[\s-]+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

export function ChatSurface() {
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [selected, setSelected] = useState<string>(() => {
    try { return localStorage.getItem('beast:sessions-selected') || GENERAL; } catch { return GENERAL; }
  });

  useEffect(() => {
    let live = true;
    const pull = () => fetch(`${GW}/sessions`).then(r => r.json())
      .then(d => { if (live && d.ok) setSessions(d.sessions); }).catch(() => {});
    pull();
    const t = setInterval(pull, 5000);
    return () => { live = false; clearInterval(t); };
  }, []);

  const select = (slug: string) => {
    setSelected(slug);
    try { localStorage.setItem('beast:sessions-selected', slug); } catch {}
  };

  /* VOICE ROUTING (Boss 2026-07-11 'send my transcription into whatever session is open'):
     publish the on-screen session so the orb's turn lands where Boss is looking, not always
     the hidden main chat. Each chat component only accepts a voice-send when it's this target. */
  useEffect(() => { (window as any).__beastVoiceTarget = selected; }, [selected]);

  const rows = [
    { slug: BEAST, name: 'Beast', ini: '⚡', hue: HUES[0], meta: 'MAIN · CLASSIC CHAT', working: false },
    { slug: GENERAL, name: 'General', ini: 'G', hue: HUES[4], meta: 'MAIN SESSION', working: false },
    ...sessions.map(s => ({
      slug: s.slug, name: s.name, ini: iniOf(s.name), hue: hueOf(s.slug),
      meta: `${s.state.toUpperCase()}${s.contextUsage ? ` · CTX ${Math.round(s.contextUsage.percent)}%` : ''}`,
      working: s.state === 'working',
    })),
  ];
  const cur = rows.find(r => r.slug === selected) || rows[1];

  return (
    <div className="relative flex h-full gap-8" style={{ minHeight: 0 }}>
      <style>{`
        @keyframes uifChatPulse { 0%,100% { opacity: 1 } 50% { opacity: .35 } }
        @keyframes bmWave { 0%,100% { transform: scaleY(.3) } 50% { transform: scaleY(1) } }
        @keyframes bmRing { 0% { transform: scale(.6); opacity: .8 } 100% { transform: scale(1.5); opacity: 0 } }
        /* strip classic chrome off the hosted chat components — transcript + input only */
        .uif-chat > div { height: 100% !important; background: transparent !important; }
        .uif-chat > div > div:first-child { display: none; } /* classic header bar */
      `}</style>

      {/* left — parallel sessions rail (spec: left 64→300w) */}
      <div className="flex flex-col gap-[9px]" style={{ width: 300, flex: 'none', overflowY: 'auto', scrollbarWidth: 'thin' }}>
        <div style={{ ...mono(18, '.3em'), color: '#9ef0ff', textShadow: '0 0 10px rgba(0,240,255,.4)', marginBottom: 5 }}>PARALLEL SESSIONS</div>
        {rows.map(r => (
          <button key={r.slug} onMouseUp={() => select(r.slug)}
            style={{
              display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', borderRadius: 12, textAlign: 'left',
              border: `1px solid rgba(125,232,255,${r.slug === selected ? '.5' : '.14'})`,
              background: r.slug === selected ? 'linear-gradient(170deg,rgba(40,140,180,.14),rgba(8,18,28,.6))' : 'rgba(8,18,28,.4)',
              boxShadow: r.slug === selected ? '0 0 30px -8px rgba(0,240,255,.35)' : 'none',
              transition: 'all .25s', flex: 'none',
            }}>
            <span style={{ width: 30, height: 30, borderRadius: 9, border: `1px solid rgba(${r.hue.rgb},.5)`, background: `rgba(${r.hue.rgb},.08)`, display: 'grid', placeItems: 'center', fontFamily: 'var(--uif-font-display)', fontSize: 17, color: r.hue.hex, flex: 'none' }}>{r.ini}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 400, fontSize: 24, color: '#e9f9ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
              <span style={{ display: 'block', ...mono(14, '.14em'), color: '#56788c', marginTop: 3 }}>{r.meta}</span>
            </span>
            {r.working && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#5df2b4', boxShadow: '0 0 8px rgba(93,242,180,.8)', flex: 'none', animation: 'uifChatPulse 2.2s infinite' }} />}
          </button>
        ))}
      </div>

      {/* center — header + hosted live chat */}
      <div className="flex flex-col" style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 4px 12px', borderBottom: '1px solid rgba(125,232,255,.14)', flex: 'none' }}>
          <span style={{ width: 34, height: 34, borderRadius: 10, border: `1px solid rgba(${cur.hue.rgb},.5)`, background: `rgba(${cur.hue.rgb},.08)`, display: 'grid', placeItems: 'center', fontFamily: 'var(--uif-font-display)', fontSize: 19, color: cur.hue.hex }}>{cur.ini}</span>
          <span style={{ fontWeight: 400, fontSize: 28, color: '#e9f9ff' }}>{cur.name}</span>
          <span style={{ ...mono(15, '.16em'), color: cur.working ? '#5df2b4' : '#56788c' }}>{cur.working ? '● LIVE' : '○ IDLE'}</span>
          <span style={{ flex: 1 }} />
          {/* FULL classic status bar (Boss 2026-07-12 'match classic UI — all elements incl
              percentages'): the hand-rolled MODEL/CTX spans only showed two of the classic
              bar's fields. Mount the real StatusBar so cost, duration, keepalive, msgs, and the
              5h/7d rate-limit percentages all render identically to classic chat. */}
          <StatusBar />
        </div>
        <div className="uif-chat" style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', paddingTop: 8 }}>
          {/* Chat stays MOUNTED always — its useChat owns the beast:voice-send pipeline
              that Beast Mode voice rides; unmounting it would kill voice sends. */}
          <div style={{ display: selected === BEAST ? 'contents' : 'none' }}><Chat key={BEAST} /></div>
          {selected === GENERAL && <SessionChat key={GENERAL} slug="general" name="General" main />}
          {selected !== BEAST && selected !== GENERAL && <SessionChat key={selected} slug={selected} name={cur.name} />}
        </div>
      </div>

      {/* right — voice console + vitals (spec right column, verbatim vox styles) */}
      <div className="flex flex-col gap-[14px]" style={{ width: 380, flex: 'none', overflowY: 'auto', scrollbarWidth: 'thin' }}>
        {/* Beast Mode voice panel removed (Boss 2026-07-11 'we don't need beast mode here, it's
            already in the orb'): it mounted a SECOND BeastMode recorder+speaker alongside the orb's,
            so every voice turn was transcribed twice and spoken twice. The orb is now the single
            Beast Mode. The fake 'VOICE VERIFIED · BOSS' badge (a hardcoded label, never a real
            check) went with it. Chat below stays mounted — its useChat still owns the
            beast:voice-send pipeline the orb rides. */}
        <div style={{ position: 'relative', borderRadius: 14, border: '1px solid rgba(125,232,255,.2)', background: 'linear-gradient(170deg,rgba(40,140,180,.07),rgba(8,18,28,.45) 55%)', padding: 16, overflow: 'hidden', flex: 1, overflowY: 'auto', scrollbarWidth: 'thin' }}>
          <div style={{ ...mono(16, '.24em'), color: '#7fa9bd' }}>SESSION VITALS</div>
          {sessions.map(s => {
            const pct = s.contextUsage ? Math.round(s.contextUsage.percent) : 0;
            const c = s.lastError ? '#ff7d8a' : pct > 45 ? '#ffc36b' : '#5df2b4';
            return (
              <div key={s.slug} style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ ...mono(16, '.16em'), color: '#56788c' }}>{s.name.toUpperCase().slice(0, 22)}</span>
                  <span style={{ ...mono(18), color: c }}>{s.lastError ? 'ERROR' : `${s.state.toUpperCase()} · ${pct}%`}</span>
                </div>
                <div style={{ marginTop: 6, height: 2, background: 'rgba(125,232,255,.08)' }}>
                  <div style={{ height: 2, width: `${Math.min(pct, 100)}%`, background: c, boxShadow: `0 0 8px ${c}`, transition: 'width .6s' }} />
                </div>
              </div>
            );
          })}
          {!sessions.length && <div style={{ ...mono(16), color: '#56788c', marginTop: 12 }}>GATEWAY OFFLINE — NO LIVE SESSIONS</div>}
        </div>
      </div>
    </div>
  );
}
